import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { authPrimaryCtaSx } from '@components/layout/AuthFormCard';
import { brandColors } from '@config/brandTokens';
import { useAuthStore } from '@store/authStore';
import { User } from '@types/index';

type ParentRole = 'mother' | 'father' | 'guardian';
type MaritalChoice = 'individual' | 'married';

const ROLE_LABEL: Record<ParentRole, string> = {
  mother: 'Mother',
  father: 'Father',
  guardian: 'Guardian',
};

function token() {
  return localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
}

interface FamilyOnboardingFormProps {
  onSaved?: (user: User) => void;
}

/** Individual / Married family status form. Individuals cannot add a child. */
const FamilyOnboardingForm: React.FC<FamilyOnboardingFormProps> = ({ onSaved }) => {
  const { user, setUser } = useAuthStore();
  const inferredRole: ParentRole =
    String(user?.gender || '').toLowerCase() === 'female'
      ? 'mother'
      : String(user?.gender || '').toLowerCase() === 'male'
        ? 'father'
        : 'guardian';

  const [maritalStatus, setMaritalStatus] = useState<MaritalChoice | ''>('');
  const [hasChildren, setHasChildren] = useState<'' | 'yes' | 'no'>('');
  const [childCount, setChildCount] = useState('1');
  const [parentRole, setParentRole] = useState<ParentRole>(
    (user?.parentRole as ParentRole) || inferredRole,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!maritalStatus) {
      setError('Choose Individual or Married.');
      return;
    }
    if (maritalStatus === 'married') {
      if (!hasChildren) {
        setError('Say whether you have children.');
        return;
      }
      if (hasChildren === 'yes') {
        const count = Number(childCount);
        if (!Number.isFinite(count) || count < 1) {
          setError('Enter how many children you have.');
          return;
        }
      }
      if (!parentRole) {
        setError('Choose whether you are the mother, father, or guardian.');
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const intended = maritalStatus === 'individual'
        ? 0
        : hasChildren === 'no'
          ? 0
          : Math.max(1, Number(childCount) || 1);
      const res = await fetch('/api/household/onboarding', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({
          maritalStatus,
          hasChildren: maritalStatus === 'married' ? hasChildren === 'yes' : false,
          intendedChildCount: intended,
          parentRole: maritalStatus === 'married' ? parentRole : null,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || 'Could not save');
      const data = json.data || {};
      const next = {
        ...(user || {}),
        maritalStatus: data.maritalStatus ?? maritalStatus,
        parentRole: data.parentRole ?? (maritalStatus === 'married' ? parentRole : null),
        parentRoleSaved: !!data.parentRoleSaved,
        intendedChildCount: data.intendedChildCount ?? intended,
        canAddChildren: data.canAddChildren ?? user?.canAddChildren,
        canCreateChild: !!data.canCreateChild,
        needsFamilyOnboarding: false,
      } as User;
      if (user) {
        setUser(next);
        localStorage.setItem('user', JSON.stringify(next));
      }
      toast.success(json.message || 'Saved.');
      onSaved?.(next);
    } catch (err: any) {
      setError(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Typography sx={{ fontWeight: 700, color: brandColors.navy, mb: 1 }}>
        You are
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        {(['individual', 'married'] as const).map((choice) => (
          <Button
            key={choice}
            variant={maritalStatus === choice ? 'contained' : 'outlined'}
            onClick={() => {
              setMaritalStatus(choice);
              setError('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              flex: 1,
              ...(maritalStatus === choice
                ? { bgcolor: brandColors.navy, '&:hover': { bgcolor: brandColors.navy } }
                : { color: brandColors.navy, borderColor: brandColors.navy }),
            }}
          >
            {choice === 'individual' ? 'Individual' : 'Married'}
          </Button>
        ))}
      </Stack>

      {maritalStatus === 'individual' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Individual accounts do not add children.
        </Typography>
      )}

      {maritalStatus === 'married' && (
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: brandColors.navy, mb: 1 }}>
              Do you have children?
            </Typography>
            <Stack direction="row" spacing={1}>
              {(['yes', 'no'] as const).map((choice) => (
                <Button
                  key={choice}
                  variant={hasChildren === choice ? 'contained' : 'outlined'}
                  onClick={() => setHasChildren(choice)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    flex: 1,
                    ...(hasChildren === choice
                      ? { bgcolor: brandColors.navy, '&:hover': { bgcolor: brandColors.navy } }
                      : { color: brandColors.navy, borderColor: brandColors.navy }),
                  }}
                >
                  {choice === 'yes' ? 'Yes' : 'Not yet'}
                </Button>
              ))}
            </Stack>
          </Box>
          {hasChildren === 'yes' && (
            <TextField
              type="number"
              label="How many children"
              value={childCount}
              onChange={(e) => setChildCount(e.target.value)}
              inputProps={{ min: 1, max: 20 }}
              fullWidth
            />
          )}
          <TextField
            select
            label="Your relation"
            value={parentRole}
            onChange={(e) => setParentRole(e.target.value as ParentRole)}
            fullWidth
          >
            {(Object.keys(ROLE_LABEL) as ParentRole[]).map((role) => (
              <MenuItem key={role} value={role}>
                {ROLE_LABEL[role]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      <Button
        fullWidth
        variant="contained"
        disabled={saving || !maritalStatus}
        onClick={save}
        sx={authPrimaryCtaSx}
      >
        {saving ? 'Saving...' : 'Continue'}
      </Button>
    </>
  );
};

export default FamilyOnboardingForm;
